import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import MarkdownRenderer from '../common/MarkdownRenderer';
import HighlightedTextDisplay from '../common/HighlightedTextDisplay';
import { compareContent } from '../../utils/contentComparison';

const ImprovementModal = ({
  open,
  onClose,
  onUpdate,
  section,
  improvedContent,
  isGenerating,
  isSpecificSection = false,
  specificSectionName = null
}) => {
  const [showComparison, setShowComparison] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (open && section) {
      console.log('ImprovementModal opened with section:', section);
      console.log('isSpecificSection:', isSpecificSection);
      console.log('specificSectionName:', specificSectionName);
      console.log('originalSectionContent:', section.originalSectionContent);
    }
  }, [open, section, isSpecificSection, specificSectionName]);

  useEffect(() => {
    if (!open) {
      setShowComparison(false);
      setIsUpdating(false);
    }
  }, [open]);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await onUpdate();
      // Modal will be closed by parent component
    } catch (error) {
      // Error handling is done by parent component
      setIsUpdating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!isGenerating && !isUpdating ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '400px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        <Typography variant="h6">
          {isSpecificSection && specificSectionName === 'text fragment' ? 
            'Improve Text Fragment' :
            isSpecificSection && specificSectionName ? 
            `Improve ${specificSectionName.charAt(0).toUpperCase() + specificSectionName.slice(1)} Section` : 
            `Improve ${section?.title || 'Section'}`}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {isGenerating ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '250px',
            gap: 2
          }}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">
              Generating improvements...
            </Typography>
          </Box>
        ) : (
          <Box>
            {showComparison ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Original
                  </Typography>
                  <Box sx={{
                    p: 2,
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    maxHeight: '400px',
                    overflow: 'auto',
                    '& p': {
                      marginBottom: '0.5em',
                    },
                    '& p:empty': {
                      height: '1em',
                    }
                  }}>
                    {section?.subsectionHeader && (
                      <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                          {section.subsectionHeader.replace(/^###\s*/, '')}
                        </Typography>
                      </Box>
                    )}
                    <MarkdownRenderer content={String(
                      isSpecificSection && section?.originalSectionContent ?
                      section.originalSectionContent :
                      (section?.content || '')
                    )} />
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Improved
                  </Typography>
                  <Box sx={{
                    p: 2,
                    backgroundColor: 'primary.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'primary.200',
                    maxHeight: '400px',
                    overflow: 'auto',
                    '& p': {
                      marginBottom: '0.5em',
                    },
                    '& p:empty': {
                      height: '1em',
                    }
                  }}>
                    {section?.subsectionHeader && (
                      <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {section.subsectionHeader.replace(/^###\s*/, '')}
                        </Typography>
                      </Box>
                    )}
                    {(() => {
                      const originalContent = isSpecificSection && section?.originalSectionContent
                        ? section.originalSectionContent
                        : (section?.content || '');
                      const improved = String(improvedContent || '');
                      const comparison = compareContent(originalContent, improved);

                      return (
                        <Box sx={{
                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                          fontSize: '0.875rem',
                          lineHeight: 1.8,
                          '& .diff-added': {
                            backgroundColor: 'rgba(76, 175, 80, 0.18)',
                            padding: '1px 3px',
                            borderRadius: '3px',
                          }
                        }}>
                          {comparison.segments.map((segment, idx) => {
                            // Render text with proper line breaks and bold formatting
                            const text = segment.text;
                            // Convert **bold** to actual bold
                            const parts = text.split(/(\*\*[^*]+\*\*)/g);
                            return (
                              <span key={idx} className={segment.isAdded ? 'diff-added' : ''}>
                                {parts.map((part, pidx) => {
                                  if (part.startsWith('**') && part.endsWith('**')) {
                                    return <strong key={pidx}>{part.slice(2, -2)}</strong>;
                                  }
                                  // Handle line breaks
                                  return part.split('\n').map((line, lidx, arr) => (
                                    <React.Fragment key={`${pidx}-${lidx}`}>
                                      {line}
                                      {lidx < arr.length - 1 && <br />}
                                    </React.Fragment>
                                  ));
                                })}
                              </span>
                            );
                          })}
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                minHeight: '250px',
                maxHeight: '500px',
                overflow: 'auto',
                '& p': {
                  marginBottom: '0.5em',
                },
                '& p:empty': {
                  height: '1em',
                },
                '& strong': {
                  fontWeight: 'bold',
                },
                '& ul': {
                  marginTop: '0.5em',
                  paddingLeft: '1.5em',
                },
                '& li': {
                  marginBottom: '0.25em',
                }
              }}>
                {section?.subsectionHeader && (
                  <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {section.subsectionHeader.replace(/^###\s*/, '')}
                    </Typography>
                  </Box>
                )}
                <MarkdownRenderer content={String(improvedContent || '')} />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        {!isGenerating && !isUpdating && improvedContent && (
          <Button
            variant="text"
            onClick={() => setShowComparison(!showComparison)}
            sx={{ mr: 'auto' }}
          >
            {showComparison ? 'Hide' : 'Show'} Comparison
          </Button>
        )}
        <Button onClick={onClose} disabled={isGenerating || isUpdating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={isGenerating || isUpdating || !improvedContent}
          startIcon={isUpdating ? <CircularProgress size={20} /> : null}
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImprovementModal;